import onReady from 'Common/core/onReady';
import { getAntiForgeryToken } from 'Common/utils/Api';

function toInt(value, fallback) {
    const parsed = Number.parseInt(value, 10);

    return Number.isFinite(parsed) ? parsed : fallback;
}

class ArticleGrid {
    constructor(root) {
        this.root = root;
        this.uid = root.dataset.uid || '';

        this.batchSize = toInt(root.dataset.pageSize, 9);
        this.pageNumber = toInt(root.dataset.pageNumber, 0);
        this.totalResultCount = toInt(root.dataset.totalResultCount, 0);
        this.resultCount = Math.min(this.totalResultCount, (this.pageNumber + 1) * this.batchSize);

        this.defaultSort = root.dataset.defaultSort || 'PageSortIndex';
        this.sortOrder = root.dataset.initialSort || this.defaultSort;
        this.query = root.dataset.initialQuery || '';

        this.categoriesMatch = root.dataset.categoriesMatch || 'Any';
        this.initialCategoryIds = root.dataset.categoryIds || '';
        this.categoryIds = this.initialCategoryIds;

        this.loadMoreUrl = root.dataset.loadMoreUrl || '/ArticlePage/LoadMoreArticles';
        this.requestVerificationToken = getAntiForgeryToken();

        this.cardFrontTitleTextColor = root.dataset.cardFrontTitleTextColor || 'light';
        this.cardBackBackgroundColor = root.dataset.cardBackBackgroundColor || 'dark';
        this.cardBackTitleTextColor = root.dataset.cardBackTitleTextColor || 'primary';
        this.cardBackTextColor = root.dataset.cardBackTextColor || 'light';
        this.cardFrontSubTitleTextColor = root.dataset.cardFrontSubTitleTextColor || 'primary';

        this.cardsGrid = root.querySelector('[data-role="cards-grid"]');
        this.pagingSummary = root.querySelector('[data-role="paging-summary"]');
        this.searchInput = root.querySelector('[data-role="search-input"]');
        this.searchButton = root.querySelector('[data-role="search-btn"]');
        this.sortLabel = root.querySelector('[data-role="sort-label"]');
        this.sortItems = Array.from(root.querySelectorAll('[data-role="sort-menu"] [data-sortby]'));
        this.filterCheckboxes = Array.from(root.querySelectorAll('[data-role="filter-checkbox"]'));
        this.filtersRow = root.querySelector('[data-role="filters-row"]');
        this.filteredBy = root.querySelector('[data-role="filtered-by"]');
        this.filtersReset = root.querySelector('[data-role="filters-reset"]');
        this.loadMoreElement = root.querySelector('[data-role="load-more"]');
        this.loadMoreSpinner = root.querySelector('[data-role="load-more-spinner"]');
        this.noResultsTemplate = root.querySelector('[data-role="no-results-template"]');

        this.isLoading = false;
        this.observer = null;

        if (!this.cardsGrid) return;

        this.bindEvents();
        this.setupObserver();
        this.renderPagingSummary();
        this.updateLoadMoreVisibility();
        this.showSpinner(false);
    }

    bindEvents() {
        if (this.searchButton) {
            this.searchButton.addEventListener('click', () => {
                this.applySearch();
            });
        }

        if (this.searchInput) {
            this.searchInput.addEventListener('keydown', (event) => {
                if (event.key !== 'Enter') return;
                event.preventDefault();
                this.applySearch();
            });
        }

        this.sortItems.forEach((sortItem) => {
            sortItem.addEventListener('click', (event) => {
                event.preventDefault();
                const nextSortOrder = sortItem.dataset.sortby || this.defaultSort;

                this.sortOrder = nextSortOrder;
                this.updateQueryParam('o', this.sortOrder);

                if (this.sortLabel) {
                    this.sortLabel.textContent = sortItem.textContent?.trim() || this.sortOrder;
                }

                this.reloadArticles();
            });
        });

        this.filterCheckboxes.forEach((checkbox) => {
            checkbox.addEventListener('change', () => {
                this.updateFilters();
            });
        });

        if (this.filtersReset) {
            this.filtersReset.addEventListener('click', () => {
                this.filterCheckboxes.forEach((checkbox) => {
                    checkbox.checked = false;
                });

                this.categoryIds = this.initialCategoryIds || this.getAllFilterCategoryIds();
                this.renderFilterChips();
                this.reloadArticles();
            });
        }

        if (this.filteredBy) {
            this.filteredBy.addEventListener('click', (event) => {
                const removeButton = event.target.closest('.remove-filter');

                if (!removeButton) return;

                const categoryName = removeButton.dataset.category || '';

                this.filterCheckboxes
                    .filter((checkbox) => checkbox.value === categoryName)
                    .forEach((checkbox) => {
                        checkbox.checked = false;
                    });

                this.updateFilters();
            });

            this.filteredBy.addEventListener('keydown', (event) => {
                if (event.key !== 'Enter' && event.key !== ' ') return;
                const removeButton = event.target.closest('.remove-filter');

                if (!removeButton) return;

                event.preventDefault();
                removeButton.click();
            });
        }
    }

    setupObserver() {
        if (!this.loadMoreElement || typeof IntersectionObserver === 'undefined') return;

        this.observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (!entry.isIntersecting || !this.canLoadMore() || this.isLoading) return;
                    this.loadMoreArticles();
                });
            },
            {
                root: null,
                rootMargin: '160px 0px',
                threshold: 0.01,
            }
        );

        this.observer.observe(this.loadMoreElement);
    }

    canLoadMore() {
        return this.resultCount < this.totalResultCount;
    }

    showSpinner(show) {
        if (!this.loadMoreSpinner) return;
        this.loadMoreSpinner.style.display = show ? '' : 'none';
    }

    renderPagingSummary() {
        if (!this.pagingSummary) return;

        if (this.totalResultCount <= 0) {
            this.pagingSummary.classList.add('d-none');
            this.pagingSummary.textContent = '';
            return;
        }

        const pagingCount = Math.min(this.totalResultCount, this.resultCount);

        this.pagingSummary.classList.remove('d-none');
        this.pagingSummary.textContent = `Showing 1 - ${pagingCount} of ${this.totalResultCount} results`;
    }

    updateLoadMoreVisibility() {
        if (!this.loadMoreElement) return;

        const hasMoreResults = this.canLoadMore();
        
        this.loadMoreElement.classList.toggle('d-none', !hasMoreResults);
    }

    updateQueryParam(key, value) {
        const params = new URLSearchParams(window.location.search);

        if (value) {
            params.set(key, value);
        } else {
            params.delete(key);
        }

        const query = params.toString();
        const nextUrl = `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`;

        window.history.pushState({ path: nextUrl }, '', nextUrl);
    }

    getAllFilterCategoryIds() {
        return this.filterCheckboxes.map((checkbox) => checkbox.dataset.categoryId).filter(Boolean).join(',');
    }

    getSelectedFilterCategoryIds() {
        return this.filterCheckboxes
            .filter((checkbox) => checkbox.checked)
            .map((checkbox) => checkbox.dataset.categoryId)
            .filter(Boolean);
    }

    updateFilters() {
        const selectedCategoryIds = this.getSelectedFilterCategoryIds();

        this.categoryIds = selectedCategoryIds.join(',');

        if (!this.categoryIds) {
            this.categoryIds = this.initialCategoryIds || this.getAllFilterCategoryIds();
        }

        this.renderFilterChips();
        this.reloadArticles();
    }

    renderFilterChips() {
        if (!this.filteredBy || !this.filtersRow) return;

        const selectedCategories = this.filterCheckboxes
            .filter((checkbox) => checkbox.checked)
            .map((checkbox) => checkbox.value)
            .filter(Boolean);

        this.filteredBy.innerHTML = '';

        if (!selectedCategories.length) {
            this.filtersRow.classList.add('d-none');
            return;
        }

        this.filtersRow.classList.remove('d-none');

        selectedCategories.forEach((categoryName) => {
            const chip = document.createElement('span');

            chip.className = 'btn btn-dark btn-sm';
            const removeIcon = document.createElement('i');

            removeIcon.className = 'fas fa-times-circle remove-filter';
            removeIcon.dataset.category = categoryName;
            removeIcon.setAttribute('role', 'button');
            removeIcon.setAttribute('tabindex', '0');
            removeIcon.setAttribute('aria-label', `Remove ${categoryName}`);
            chip.appendChild(removeIcon);
            chip.appendChild(document.createTextNode(categoryName));
            this.filteredBy.appendChild(chip);
        });
    }

    applySearch() {
        this.query = this.searchInput?.value?.trim() || '';
        this.updateQueryParam('q', this.query);
        this.reloadArticles();
    }

    reloadArticles() {
        this.pageNumber = 0;
        this.resultCount = 0;
        this.loadArticles(0, this.batchSize, false);
    }

    loadMoreArticles() {
        if (!this.canLoadMore() || this.isLoading) return;

        const nextPage = this.pageNumber + 1;

        this.loadArticles(nextPage, this.batchSize, true);
    }

    getNoResultsHtml() {
        const content = this.noResultsTemplate?.innerHTML || '';

        return content.trim();
    }

    async loadArticles(pageNumber, batchSize, isLoadMore) {
        if (this.isLoading) return;

        this.isLoading = true;
        this.showSpinner(true);

        try {
            const body = new URLSearchParams({
                Query: this.query,
                PageNumber: String(pageNumber),
                BatchSize: String(batchSize),
                SortOrder: this.sortOrder,
                CategoryIds: this.categoryIds,
                CategoriesMatch: this.categoriesMatch,
                CardFrontTitleTextColor: this.cardFrontTitleTextColor,
                CardBackBackgroundColor: this.cardBackBackgroundColor,
                CardBackTitleTextColor: this.cardBackTitleTextColor,
                CardBackTextColor: this.cardBackTextColor,
                CardFrontSubTitleTextColor: this.cardFrontSubTitleTextColor,
            });

            const headers = {
                'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
            };

            if (this.requestVerificationToken) {
                headers.RequestVerificationToken = this.requestVerificationToken;
            }

            const response = await fetch(this.loadMoreUrl, {
                method: 'POST',
                credentials: 'same-origin',
                headers,
                body: body.toString(),
            });

            if (!response.ok) return;

            const result = await response.json();
            const totalResultCount = toInt(
                result.TotalResultCount ?? result.totalResultCount,
                this.totalResultCount
            );

            const articlesHtml = (result.ArticlesHtml ?? result.articlesHtml ?? '').trim();

            if (isLoadMore) {
                if (articlesHtml) {
                    this.cardsGrid.insertAdjacentHTML('beforeend', articlesHtml);
                }
            } else {
                this.cardsGrid.innerHTML = articlesHtml || this.getNoResultsHtml();
            }

            this.pageNumber = pageNumber;
            this.totalResultCount = totalResultCount;
            this.resultCount = Math.min(this.totalResultCount, (this.pageNumber + 1) * batchSize);

            this.renderPagingSummary();
            this.updateLoadMoreVisibility();
        } catch (error) {
            console.warn('Article grid load failed', error);
        } finally {
            this.isLoading = false;
            this.showSpinner(false);
        }
    }
}

export default function articleGrid() {
    onReady(() => {
        document.querySelectorAll('.article-grid[data-uid]').forEach((root) => {
            if (root.dataset.articleGridInitialized === 'true') return;
            root.dataset.articleGridInitialized = 'true';
            new ArticleGrid(root);
        });
    });
}

